import { motion } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { useInView } from 'react-intersection-observer';
import { useLocation, useNavigate } from 'react-router-dom';
import './Blogs.css';
import { getApiUrl } from '../utils/api';

let blogsCache = null;

const Blogs = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const scrollContainerRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const [activeBlogIndex, setActiveBlogIndex] = useState(0);
  const [blogs, setBlogs] = useState(blogsCache || []);

  useEffect(() => {
    // Added a limit query parameter to fetch up to 100 blogs instead of defaulting to 10
    const url = getApiUrl('/api/blogs?limit=100');
    fetch(url)
      .then(res => res.json())
      .then(data => {
        console.log("Fetched blogs data:", data);
        if (data && Array.isArray(data.data)) {
          blogsCache = data.data;
          setBlogs(data.data);
        }
      })
      .catch(err => console.error("Error fetching blogs:", err));
  }, []);

  useEffect(() => {
    if (blogs.length > 0 && location.state?.scrollTo === 'blogs') {
      setTimeout(() => {
        const element = document.getElementById('blogs');
        if (element) {
          if (window.lenis) {
            window.lenis.scrollTo(element);
          } else {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }
      }, 50);

      if (location.state?.activeBlogId) {
        setTimeout(() => {
          const container = scrollContainerRef.current;
          const card = document.getElementById(`blog-${location.state.activeBlogId}`);
          if (container && card) {
            const containerWidth = container.clientWidth;
            const cardWidth = card.clientWidth;
            const cardOffsetLeft = card.offsetLeft;
            const scrollPosition = cardOffsetLeft - (containerWidth / 2) + (cardWidth / 2);
            container.scrollTo({
              left: scrollPosition,
              behavior: 'smooth'
            });
          }
        }, 150);
      }
    }
  }, [blogs, location.state]);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Disable animations on mobile - show immediately
  const shouldAnimate = !isMobile;

  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: shouldAnimate ? {
        staggerChildren: 0.1
      } : {}
    }
  };

  const itemVariants = {
    hidden: { y: shouldAnimate ? 30 : 0, opacity: shouldAnimate ? 0 : 1 },
    visible: {
      y: 0,
      opacity: 1,
      transition: shouldAnimate ? {
        duration: 0.6
      } : { duration: 0 }
    }
  };


  const handleBlogClick = (blogId) => {
    navigate(`/blog/${blogId}`);
  };


  // Scroll functions for both desktop and mobile
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const isMobile = window.innerWidth <= 768;
      
      const cardWidth = isMobile ? (window.innerWidth - 0) : 400;
      const gap = isMobile ? 20 : 0;
      const scrollAmount = isMobile ? (cardWidth + gap) : 400;
      
      if (container.scrollLeft <= 10) {
        // If at the beginning, wrap around to the end of the scroll list
        const maxScrollLeft = container.scrollWidth - container.clientWidth;
        container.scrollTo({
          left: maxScrollLeft,
          behavior: 'smooth'
        });
      } else {
        container.scrollBy({
          left: -scrollAmount,
          behavior: 'smooth'
        });
      }
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const isMobile = window.innerWidth <= 768;
      
      const cardWidth = isMobile ? (window.innerWidth - 0) : 400;
      const gap = isMobile ? 20 : 0;
      const scrollAmount = isMobile ? (cardWidth + gap) : 400;
      
      const maxScrollLeft = container.scrollWidth - container.clientWidth;
      if (container.scrollLeft >= maxScrollLeft - 10) {
        // If at the end, wrap around to the beginning
        container.scrollTo({
          left: 0,
          behavior: 'smooth'
        });
      } else {
        container.scrollBy({
          left: scrollAmount,
          behavior: 'smooth'
        });
      }
    }
  };

  // Track active blog index based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      const container = scrollContainerRef.current;
      if (container) {
        const cards = container.querySelectorAll('.blog-card');
        const containerWidth = container.clientWidth;
        
        // Find the most visible card for pagination dots
        let mostVisibleIndex = 0;
        let maxVisibleArea = 0;
        
        cards.forEach((card, index) => {
          const cardRect = card.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          const cardLeft = cardRect.left - containerRect.left;
          const cardRight = cardRect.right - containerRect.left;
          
          // Calculate visible area
          const visibleLeft = Math.max(0, cardLeft);
          const visibleRight = Math.min(containerWidth, cardRight);
          const visibleArea = Math.max(0, visibleRight - visibleLeft);
          
          if (visibleArea > maxVisibleArea) {
            maxVisibleArea = visibleArea;
            mostVisibleIndex = index;
          }
        });
        
        // Update active blog index
        setActiveBlogIndex(mostVisibleIndex);
      }
    };

    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleScroll);
      // Initial call
      handleScroll();
      
      return () => {
        container.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleScroll);
      };
    }
  }, []);

  return (
    <section id="blogs" className="blogs" ref={ref}>
      <motion.div
        className="blogs-container"
        variants={containerVariants}
        initial={shouldAnimate ? "hidden" : "visible"}
        animate={shouldAnimate ? (inView ? "visible" : "hidden") : "visible"}
      >
        <motion.div className="blogs-header" variants={itemVariants}>
          <div className="blogs-header-content">
            <div className="blogs-header-left"></div>
            <div className="blogs-header-center">
              <div className="blogs-title-container">
                <h2 className="blogs-title">Latest Insights</h2>
               
              </div>
              <p className="blogs-subtitle">
                Stay updated with the latest trends and insights in technology and development
              </p>
              <motion.div className="blogs-navigation" variants={itemVariants}>
                  <button className="scroll-btn scroll-left" onClick={scrollLeft}>
                    <FaChevronLeft />
                  </button>
                  <button className="scroll-btn scroll-right" onClick={scrollRight}>
                    <FaChevronRight />
                  </button>
                </motion.div>
            </div>
            <div className="blogs-header-right"></div>
          </div>
        </motion.div>

        <motion.div 
          className="blogs-scroll-container" 
          variants={itemVariants}
          initial={shouldAnimate ? "hidden" : "visible"}
          animate={shouldAnimate ? "visible" : "visible"}
        >
          {/* Unified horizontal scroll for both desktop and mobile */}
          <div className="blogs-scroll" ref={scrollContainerRef}>
            {blogs.map((blog, index) => (
              <motion.div
                key={blog.id}
                id={`blog-${blog.id}`}
                className="blog-card"
                variants={itemVariants}
                whileHover={{ 
                  scale: 1.02,
                  y: -5
                }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleBlogClick(blog.id)}
                style={{ '--delay': `${index * 0.1}s` }}
              >
                <div className="blogs-list-image-container">
                    <img 
                      src={blog.imageUrl || "/images/blog/default.png"} 
                      alt={blog.title}
                      className="blogs-list-image"
                    />
                </div>
                
                <div className="blogs-list-content">
                  <div className="blogs-list-meta">
                    <span className="blogs-list-category">{blog.role || "Tech"}</span>
                    <span className="blogs-list-date">{new Date(blog.createdAt).toLocaleDateString()}</span>
                  </div>
                  
                  <h3 className="blogs-list-title">{blog.title}</h3>
                  <p className="blogs-list-excerpt">{blog.content?.substring(0, 100)}...</p>
                  
                  <div className="blogs-list-footer">
                    <div className="blogs-list-author">
                      <span>By {blog.author}</span>
                    </div>
                    <div className="blogs-list-read-time">
                      {blog.readTime}
                    </div>
                  </div>
                  
                  <div className="blogs-list-tags">
                    {blog.techStack?.slice(0, 3).map((tag, tagIndex) => (
                      <span key={tagIndex} className="blogs-list-tag">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
        {/* Pagination Dots - Mobile Only */}
        {isMobile && (
          <motion.div className="blogs-pagination-dots" variants={itemVariants}>
            {blogs.map((_, index) => (
              <button
                key={index}
                className={`pagination-dot ${index === activeBlogIndex ? 'active' : ''}`}
                onClick={() => {
                  if (scrollContainerRef.current) {
                    const container = scrollContainerRef.current;
                    const cards = container.querySelectorAll('.blog-card');
                    
                    if (cards[index]) {
                      const cardWidth = window.innerWidth;
                      const gap = 20;
                      const scrollPosition = index * (cardWidth + gap);
                      
                      container.scrollTo({
                        left: scrollPosition,
                        behavior: 'smooth'
                      });
                    }
                  }
                }}
                aria-label={`Go to blog ${index + 1}`}
              />
            ))}
          </motion.div>
        )}
      </motion.div>
    </section>
  );
};

export default Blogs;
